// งานพิกเซลของตัวแก้ภาพ (ไม่ผูกกับ React)
// ลำดับ: ครอป/หมุน/พลิก/ย่อ (ทำจริงบน base canvas) -> ปรับสี -> ปิดบัง (เบลอ/โมเสก/แถบดำ) -> ลายน้ำ -> ออบเจกต์
// พิกัดออบเจกต์ทั้งหมดเป็นพิกเซลของ base canvas (ความละเอียดเต็ม) ตอนพรีวิวคูณด้วย scale
import { TEXT_ASCENT, TEXT_LINE_HEIGHT, normRot, rotateVec } from "../pdf-editor/geometry";
import { loadImage } from "../pdf-editor/imageUtils";
import { fontString, textBox } from "../pdf-editor/textUtils";

export const MAX_CANVAS_SIDE = 16384;
export const MAX_CANVAS_AREA = 268435456;
export const PREVIEW_MAX = 2400;

export const DEFAULT_ADJUST = { brightness: 0, contrast: 0, saturation: 0, filter: "none", blur: 0, sharpen: 0 };
export const DEFAULT_WATERMARK = { enabled: false, text: "สำเนาเท่านั้น", sizePct: 6, opacity: 0.3, color: "#808080", position: "center", tile: false, angle: -30 };

export const FILTERS = [
  { key: "none", label: "ปกติ" },
  { key: "gray", label: "ขาวดำ" },
  { key: "sepia", label: "ซีเปีย" },
  { key: "warm", label: "อุ่น" },
  { key: "cool", label: "เย็น" },
  { key: "fade", label: "ซีด" },
  { key: "vivid", label: "สดใส" },
  { key: "vintage", label: "ย้อนยุค" },
  { key: "invert", label: "กลับสี" },
];

export const makeCanvas = (w, h) => {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
};

export const cloneCanvas = (src) => {
  const c = makeCanvas(src.width, src.height);
  c.getContext("2d", { willReadFrequently: true }).drawImage(src, 0, 0);
  return c;
};

export const exceedsCanvasLimit = (w, h) => w > MAX_CANVAS_SIDE || h > MAX_CANVAS_SIDE || w * h > MAX_CANVAS_AREA;

// หน่วยความหนา/เบลอ: 1 unit = 1/1000 ของด้านยาว
export const unitOf = (canvas) => Math.max(canvas.width, canvas.height) / 1000;

/* ---------------- เรขาคณิต (ทำจริงบน canvas) ---------------- */

export function cropCanvas(src, r) {
  const out = makeCanvas(r.w, r.h);
  out.getContext("2d").drawImage(src, r.x, r.y, r.w, r.h, 0, 0, out.width, out.height);
  return out;
}

export function rotateCanvas(src, dir) {
  const out = makeCanvas(src.height, src.width);
  const ctx = out.getContext("2d");
  if (dir > 0) {
    ctx.translate(src.height, 0);
    ctx.rotate(Math.PI / 2);
  } else {
    ctx.translate(0, src.width);
    ctx.rotate(-Math.PI / 2);
  }
  ctx.drawImage(src, 0, 0);
  return out;
}

export function flipCanvas(src, axis) {
  const out = makeCanvas(src.width, src.height);
  const ctx = out.getContext("2d");
  if (axis === "h") {
    ctx.translate(src.width, 0);
    ctx.scale(-1, 1);
  } else {
    ctx.translate(0, src.height);
    ctx.scale(1, -1);
  }
  ctx.drawImage(src, 0, 0);
  return out;
}

export function resizeCanvas(src, w, h) {
  let cur = src;
  // ย่อทีละครึ่งเพื่อคงความคม (drawImage ครั้งเดียวจากภาพใหญ่มากจะหยาบ)
  while (cur.width / 2 >= w && cur.height / 2 >= h) {
    const half = makeCanvas(cur.width / 2, cur.height / 2);
    const hc = half.getContext("2d");
    hc.imageSmoothingQuality = "high";
    hc.drawImage(cur, 0, 0, half.width, half.height);
    cur = half;
  }
  const out = makeCanvas(w, h);
  const ctx = out.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(cur, 0, 0, out.width, out.height);
  return out;
}

/* ---------------- ย้ายออบเจกต์ตามการแก้เรขาคณิต ---------------- */

const boxOf = (o) => (o.type === "text" ? textBox(o) : { w: o.w, h: o.h });
const isStroke = (o) => o.type === "stroke" || o.type === "highlight";

// op: { type: "crop", x, y } | { type: "rotate", dir: 90 | -90 } | { type: "flip", axis: "h"|"v" } | { type: "resize", sx, sy }
// dims = { w, h } ของภาพก่อนทำ op
export function transformObjects(objects, op, dims) {
  const { w: W, h: H } = dims;

  const mapPoint = (x, y) => {
    if (op.type === "crop") return { x: x - op.x, y: y - op.y };
    if (op.type === "rotate") return op.dir > 0 ? { x: H - y, y: x } : { x: y, y: W - x };
    if (op.type === "flip") return op.axis === "h" ? { x: W - x, y } : { x, y: H - y };
    return { x: x * op.sx, y: y * op.sy };
  };

  return objects.map((o) => {
    if (isStroke(o)) {
      const k = op.type === "resize" ? Math.sqrt(op.sx * op.sy) : 1;
      return { ...o, points: o.points.map((p) => mapPoint(p.x, p.y)), width: o.width * k };
    }

    if (op.type === "flip") {
      // ย้ายตำแหน่งไปฝั่งตรงข้ามแต่คงตั้งตรง (ไม่กลับด้านตัวอักษร/รูปที่วาง)
      const { w, h } = boxOf(o);
      const v = rotateVec(w / 2, h / 2, o.rot || 0);
      const c = mapPoint(o.x + v.x, o.y + v.y);
      const rot = normRot(-(o.rot || 0));
      const v2 = rotateVec(w / 2, h / 2, rot);
      return { ...o, x: c.x - v2.x, y: c.y - v2.y, rot };
    }

    const p = mapPoint(o.x, o.y);
    const next = { ...o, x: p.x, y: p.y };
    if (op.type === "rotate") next.rot = normRot((o.rot || 0) + op.dir);
    if (op.type === "resize") {
      const k = Math.sqrt(op.sx * op.sy);
      if (o.type === "text") next.size = o.size * k;
      else {
        const swap = normRot(o.rot || 0) % 180 !== 0;
        next.w = o.w * (swap ? op.sy : op.sx);
        next.h = o.h * (swap ? op.sx : op.sy);
        if (o.type === "rect") next.width = o.width * k;
        if (o.type === "redact") next.strength = o.strength; // สเกลตามหน่วยภาพอยู่แล้ว
      }
    }
    return next;
  });
}

/* ---------------- ปรับสี / เบลอ / ความคม ---------------- */

const clamp255 = (v) => (v < 0 ? 0 : v > 255 ? 255 : v);

const filterFn = (key) => {
  switch (key) {
    case "gray": return (r, g, b) => { const l = 0.299 * r + 0.587 * g + 0.114 * b; return [l, l, l]; };
    case "sepia": return (r, g, b) => [0.393 * r + 0.769 * g + 0.189 * b, 0.349 * r + 0.686 * g + 0.168 * b, 0.272 * r + 0.534 * g + 0.131 * b];
    case "warm": return (r, g, b) => [r * 1.08 + 8, g * 1.02, b * 0.88];
    case "cool": return (r, g, b) => [r * 0.9, g * 1.01, b * 1.1 + 8];
    case "fade": return (r, g, b) => [r * 0.8 + 42, g * 0.8 + 42, b * 0.8 + 46];
    case "vivid": return (r, g, b) => {
      const l = 0.299 * r + 0.587 * g + 0.114 * b;
      return [(l + (r - l) * 1.4 - 128) * 1.1 + 128, (l + (g - l) * 1.4 - 128) * 1.1 + 128, (l + (b - l) * 1.4 - 128) * 1.1 + 128];
    };
    case "vintage": return (r, g, b) => {
      const sr = 0.393 * r + 0.769 * g + 0.189 * b;
      const sg = 0.349 * r + 0.686 * g + 0.168 * b;
      const sb = 0.272 * r + 0.534 * g + 0.131 * b;
      return [(sr * 0.6 + r * 0.4) * 0.9 + 30, (sg * 0.6 + g * 0.4) * 0.9 + 26, (sb * 0.6 + b * 0.4) * 0.9 + 28];
    };
    case "invert": return (r, g, b) => [255 - r, 255 - g, 255 - b];
    default: return null;
  }
};

function boxBlurH(src, dst, w, h, r, ch) {
  const iarr = 1 / (r + r + 1);
  for (let i = 0; i < h; i += 1) {
    let ti = i * w;
    let li = ti;
    let ri = ti + r;
    const fv = src[ti * 4 + ch];
    const lv = src[(ti + w - 1) * 4 + ch];
    let val = (r + 1) * fv;
    for (let j = 0; j < r; j += 1) val += src[(ti + j) * 4 + ch];
    for (let j = 0; j <= r; j += 1) { val += src[ri * 4 + ch] - fv; ri += 1; dst[ti * 4 + ch] = Math.round(val * iarr); ti += 1; }
    for (let j = r + 1; j < w - r; j += 1) { val += src[ri * 4 + ch] - src[li * 4 + ch]; ri += 1; li += 1; dst[ti * 4 + ch] = Math.round(val * iarr); ti += 1; }
    for (let j = w - r; j < w; j += 1) { val += lv - src[li * 4 + ch]; li += 1; dst[ti * 4 + ch] = Math.round(val * iarr); ti += 1; }
  }
}

function boxBlurV(src, dst, w, h, r, ch) {
  const iarr = 1 / (r + r + 1);
  for (let i = 0; i < w; i += 1) {
    let ti = i;
    let li = ti;
    let ri = ti + r * w;
    const fv = src[ti * 4 + ch];
    const lv = src[(ti + w * (h - 1)) * 4 + ch];
    let val = (r + 1) * fv;
    for (let j = 0; j < r; j += 1) val += src[(ti + j * w) * 4 + ch];
    for (let j = 0; j <= r; j += 1) { val += src[ri * 4 + ch] - fv; ri += w; dst[ti * 4 + ch] = Math.round(val * iarr); ti += w; }
    for (let j = r + 1; j < h - r; j += 1) { val += src[ri * 4 + ch] - src[li * 4 + ch]; ri += w; li += w; dst[ti * 4 + ch] = Math.round(val * iarr); ti += w; }
    for (let j = h - r; j < h; j += 1) { val += lv - src[li * 4 + ch]; li += w; dst[ti * 4 + ch] = Math.round(val * iarr); ti += w; }
  }
}

// เบลอแบบ gaussian ประมาณด้วย box 3 รอบ (sigma หน่วยพิกเซล) บน ImageData ที่ alpha-premultiplied
export function gaussianBlurImageData(imageData, sigma) {
  const { width: w, height: h, data } = imageData;
  if (sigma < 0.4 || w < 3 || h < 3) return;
  const wIdeal = Math.sqrt((12 * sigma * sigma) / 3 + 1);
  let wl = Math.floor(wIdeal);
  if (wl % 2 === 0) wl -= 1;
  const wu = wl + 2;
  const m = Math.round((12 * sigma * sigma - 3 * wl * wl - 12 * wl - 9) / (-4 * wl - 4));
  const maxR = Math.floor((Math.min(w, h) - 1) / 2);
  const tmp = new Uint8ClampedArray(data.length);

  for (let n = 0; n < 3; n += 1) {
    const r = Math.min(maxR, ((n < m ? wl : wu) - 1) / 2);
    if (r < 1) continue;
    for (let ch = 0; ch < 4; ch += 1) {
      boxBlurH(data, tmp, w, h, r, ch);
      boxBlurV(tmp, data, w, h, r, ch);
    }
  }
}

const premultiply = (d) => {
  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3];
    if (a === 255) continue;
    d[i] = Math.round((d[i] * a) / 255);
    d[i + 1] = Math.round((d[i + 1] * a) / 255);
    d[i + 2] = Math.round((d[i + 2] * a) / 255);
  }
};

const unpremultiply = (d) => {
  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3];
    if (a === 255 || a === 0) continue;
    d[i] = Math.min(255, Math.round((d[i] * 255) / a));
    d[i + 1] = Math.min(255, Math.round((d[i + 1] * 255) / a));
    d[i + 2] = Math.min(255, Math.round((d[i + 2] * 255) / a));
  }
};

export const isNeutralAdjust = (adj) =>
  !adj.brightness && !adj.contrast && !adj.saturation && adj.filter === "none" && !adj.blur && !adj.sharpen;

// scale = สัดส่วนของ canvas นี้เทียบกับภาพเต็ม (พรีวิว < 1) เพื่อให้เบลอ/ความคมเท่ากันทั้งพรีวิวและตอนบันทึก
export function applyAdjust(canvas, adj, scale, fullUnit) {
  if (isNeutralAdjust(adj)) return;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = image.data;

  const colorChanged = adj.brightness || adj.contrast || adj.saturation || adj.filter !== "none";
  if (colorChanged) {
    const bright = adj.brightness * 2.55;
    const c255 = adj.contrast * 2.55;
    const cf = (259 * (c255 + 255)) / (255 * (259 - c255));
    const sat = 1 + adj.saturation / 100;
    const fx = filterFn(adj.filter);
    for (let i = 0; i < d.length; i += 4) {
      let r = d[i] + bright;
      let g = d[i + 1] + bright;
      let b = d[i + 2] + bright;
      if (adj.contrast) { r = cf * (r - 128) + 128; g = cf * (g - 128) + 128; b = cf * (b - 128) + 128; }
      if (adj.saturation) {
        const l = 0.299 * r + 0.587 * g + 0.114 * b;
        r = l + (r - l) * sat; g = l + (g - l) * sat; b = l + (b - l) * sat;
      }
      if (fx) [r, g, b] = fx(clamp255(r), clamp255(g), clamp255(b));
      d[i] = clamp255(r); d[i + 1] = clamp255(g); d[i + 2] = clamp255(b);
    }
  }

  if (adj.blur) {
    premultiply(d);
    gaussianBlurImageData(image, adj.blur * fullUnit * scale);
    unpremultiply(d);
  }

  if (adj.sharpen) {
    const copy = new ImageData(new Uint8ClampedArray(d), image.width, image.height);
    premultiply(copy.data);
    gaussianBlurImageData(copy, Math.max(0.8, 1.4 * fullUnit * scale));
    unpremultiply(copy.data);
    const k = adj.sharpen / 50; // 0..100 -> 0..2
    for (let i = 0; i < d.length; i += 4) {
      d[i] = clamp255(d[i] + k * (d[i] - copy.data[i]));
      d[i + 1] = clamp255(d[i + 1] + k * (d[i + 1] - copy.data[i + 1]));
      d[i + 2] = clamp255(d[i + 2] + k * (d[i + 2] - copy.data[i + 2]));
    }
  }

  ctx.putImageData(image, 0, 0);
}

/* ---------------- ปิดบังเฉพาะส่วน ---------------- */

// กรอบแกนภาพ (axis-aligned) ของออบเจกต์ที่อาจหมุนมา (หมุนทีละ 90° จึงแม่นยำ)
export function boundsOf(o) {
  const pts = [[0, 0], [o.w, 0], [o.w, o.h], [0, o.h]].map(([x, y]) => {
    const v = rotateVec(x, y, o.rot || 0);
    return { x: o.x + v.x, y: o.y + v.y };
  });
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
}

export function applyRedact(canvas, o, scale, fullUnit) {
  const b = boundsOf(o);
  const x = Math.max(0, Math.floor(b.x * scale));
  const y = Math.max(0, Math.floor(b.y * scale));
  const w = Math.min(canvas.width, Math.ceil((b.x + b.w) * scale)) - x;
  const h = Math.min(canvas.height, Math.ceil((b.y + b.h) * scale)) - y;
  if (w < 1 || h < 1) return;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const strength = (o.strength || 10) * fullUnit * scale;

  if (o.mode === "black") {
    ctx.fillStyle = "#000";
    ctx.fillRect(x, y, w, h);
    return;
  }

  const image = ctx.getImageData(x, y, w, h);
  const d = image.data;

  if (o.mode === "pixelate") {
    const block = Math.max(2, Math.round(strength));
    for (let by = 0; by < h; by += block) {
      for (let bx = 0; bx < w; bx += block) {
        const bw = Math.min(block, w - bx);
        const bh = Math.min(block, h - by);
        let r = 0, g = 0, bl = 0, a = 0;
        for (let yy = 0; yy < bh; yy += 1) {
          for (let xx = 0; xx < bw; xx += 1) {
            const i = ((by + yy) * w + bx + xx) * 4;
            r += d[i]; g += d[i + 1]; bl += d[i + 2]; a += d[i + 3];
          }
        }
        const n = bw * bh;
        r = Math.round(r / n); g = Math.round(g / n); bl = Math.round(bl / n); a = Math.round(a / n);
        for (let yy = 0; yy < bh; yy += 1) {
          for (let xx = 0; xx < bw; xx += 1) {
            const i = ((by + yy) * w + bx + xx) * 4;
            d[i] = r; d[i + 1] = g; d[i + 2] = bl; d[i + 3] = a;
          }
        }
      }
    }
  } else {
    premultiply(d);
    gaussianBlurImageData(image, Math.max(1, strength));
    unpremultiply(d);
  }
  ctx.putImageData(image, x, y);
}

/* ---------------- ลายน้ำ ---------------- */

export function drawWatermark(canvas, wm) {
  if (!wm.enabled || !String(wm.text || "").trim()) return;
  const ctx = canvas.getContext("2d");
  const W = canvas.width;
  const H = canvas.height;
  const px = Math.max(6, (wm.sizePct / 100) * Math.min(W, H));
  ctx.save();
  ctx.globalAlpha = wm.opacity;
  ctx.fillStyle = wm.color;
  ctx.font = fontString(px, true);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const text = String(wm.text).trim();
  const tw = ctx.measureText(text).width;
  const angle = (wm.angle * Math.PI) / 180;

  if (wm.tile) {
    const stepX = tw + px * 1.2;
    const stepY = px * 2.6;
    const extent = Math.hypot(W, H);
    ctx.translate(W / 2, H / 2);
    ctx.rotate(angle);
    let row = 0;
    for (let y = -extent; y <= extent; y += stepY, row += 1) {
      const offset = row % 2 ? stepX / 2 : 0;
      for (let x = -extent + offset; x <= extent; x += stepX) ctx.fillText(text, x, y);
    }
  } else {
    const m = Math.min(W, H) * 0.04;
    const pos = {
      center: [W / 2, H / 2],
      tl: [m + tw / 2, m + px / 2],
      tr: [W - m - tw / 2, m + px / 2],
      bl: [m + tw / 2, H - m - px / 2],
      br: [W - m - tw / 2, H - m - px / 2],
    }[wm.position] || [W / 2, H / 2];
    ctx.translate(pos[0], pos[1]);
    ctx.rotate(wm.position === "center" ? angle : 0);
    ctx.fillText(text, 0, 0);
  }
  ctx.restore();
}

/* ---------------- ออบเจกต์ ---------------- */

const imageCache = new Map();

export async function ensureImages(objects) {
  const srcs = objects.filter((o) => o.type === "image" && !imageCache.has(o.src)).map((o) => o.src);
  await Promise.all([...new Set(srcs)].map(async (src) => imageCache.set(src, await loadImage(src))));
}

export function drawObjects(canvas, objects, scale) {
  const ctx = canvas.getContext("2d");
  objects.forEach((o) => {
    if (o.type === "redact") return;
    ctx.save();
    ctx.scale(scale, scale);

    if (isStroke(o)) {
      if (o.points.length) {
        ctx.globalAlpha = o.type === "highlight" ? 0.35 : 1;
        ctx.strokeStyle = o.color;
        ctx.lineWidth = o.width;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.beginPath();
        if (o.points.length === 1) {
          ctx.moveTo(o.points[0].x, o.points[0].y);
          ctx.lineTo(o.points[0].x + 0.01, o.points[0].y);
        } else {
          o.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
        }
        ctx.stroke();
      }
    } else {
      ctx.translate(o.x, o.y);
      ctx.rotate(((o.rot || 0) * Math.PI) / 180);
      if (o.type === "image") {
        const img = imageCache.get(o.src);
        if (img) {
          ctx.globalAlpha = o.opacity ?? 1;
          ctx.drawImage(img, 0, 0, o.w, o.h);
        }
      } else if (o.type === "rect") {
        ctx.strokeStyle = o.color;
        ctx.lineWidth = o.width;
        ctx.strokeRect(0, 0, o.w, o.h);
      } else if (o.type === "text") {
        ctx.fillStyle = o.color;
        ctx.font = fontString(o.size, o.bold);
        ctx.textBaseline = "alphabetic";
        String(o.text || "").split("\n").forEach((line, i) => {
          if (line) ctx.fillText(line, 0, o.size * (TEXT_ASCENT + i * TEXT_LINE_HEIGHT));
        });
      }
    }
    ctx.restore();
  });
}

/* ---------------- รวมทั้งหมด ---------------- */

// พรีวิว: คืน canvas ที่มีสี+ปิดบัง+ลายน้ำ (ออบเจกต์แสดงด้วย DOM overlay)
// withObjects = true ใช้ตอนบันทึก (วาดออบเจกต์ลงพิกเซลด้วย)
export function renderPipeline({ source, scale, objects, adj, wm, fullUnit, withObjects, adjusted }) {
  const out = cloneCanvas(adjusted || source);
  if (!adjusted) applyAdjust(out, adj, scale, fullUnit);
  objects.filter((o) => o.type === "redact").forEach((o) => applyRedact(out, o, scale, fullUnit));
  drawWatermark(out, wm);
  if (withObjects) drawObjects(out, objects, scale);
  return out;
}

export const makePreviewBase = (base) => {
  const longSide = Math.max(base.width, base.height);
  if (longSide <= PREVIEW_MAX) return { canvas: base, scale: 1 };
  const scale = PREVIEW_MAX / longSide;
  return { canvas: resizeCanvas(base, Math.round(base.width * scale), Math.round(base.height * scale)), scale: PREVIEW_MAX / longSide };
};

/* ---------------- ไฟล์ออก ---------------- */

export const canvasToBlob = (canvas, mime, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("แปลงภาพไม่สำเร็จ (ภาพอาจใหญ่เกินที่เบราว์เซอร์รองรับ)"))), mime, quality);
  });

// BMP 24 บิต (ใช้ตอนบันทึกทับไฟล์ .bmp เดิม — canvas.toBlob ไม่รองรับ bmp)
export function canvasToBmpBlob(canvas) {
  const w = canvas.width;
  const h = canvas.height;
  const data = canvas.getContext("2d").getImageData(0, 0, w, h).data;
  const rowSize = Math.ceil((w * 3) / 4) * 4;
  const size = 54 + rowSize * h;
  const buf = new ArrayBuffer(size);
  const v = new DataView(buf);
  v.setUint8(0, 0x42); v.setUint8(1, 0x4d);
  v.setUint32(2, size, true); v.setUint32(10, 54, true);
  v.setUint32(14, 40, true); v.setInt32(18, w, true); v.setInt32(22, h, true);
  v.setUint16(26, 1, true); v.setUint16(28, 24, true); v.setUint32(34, rowSize * h, true);
  const px = new Uint8Array(buf);
  for (let y = 0; y < h; y += 1) {
    let o = 54 + (h - 1 - y) * rowSize;
    for (let x = 0; x < w; x += 1) {
      const i = (y * w + x) * 4;
      const a = data[i + 3] / 255;
      // BMP ไม่มี alpha -> ผสมบนพื้นขาว
      px[o++] = Math.round(data[i + 2] * a + 255 * (1 - a));
      px[o++] = Math.round(data[i + 1] * a + 255 * (1 - a));
      px[o++] = Math.round(data[i] * a + 255 * (1 - a));
    }
  }
  return new Blob([buf], { type: "image/bmp" });
}

export const flattenOnWhite = (canvas) => {
  const out = makeCanvas(canvas.width, canvas.height);
  const ctx = out.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(canvas, 0, 0);
  return out;
};
